import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import {
  FirebaseCrashlytics,
  type StackFrame as CrashlyticsStackFrame,
} from '@capacitor-firebase/crashlytics';
import { Capacitor } from '@capacitor/core';
import { filter } from 'rxjs';
import * as StackTrace from 'stacktrace-js';
import { environment } from 'src/environments/environment';

export type TelemetryEventParameters = Record<string, string | number | boolean>;

@Injectable({ providedIn: 'root' })
export class TelemetryService {
  private readonly router = inject(Router);
  private readonly telemetryConfig = environment.telemetry;
  private readonly isNative = Capacitor.isNativePlatform();
  private readonly hasFirebaseConfig = Boolean(
    environment.firebase.projectId && environment.firebase.appId
  );
  private readonly recentExceptionSignatures = new Map<string, number>();

  private initialized = false;

  initialize(): void {
    if (!this.isAvailable() || this.initialized) {
      return;
    }

    this.initialized = true;

    if (this.telemetryConfig.analyticsEnabled) {
      void FirebaseAnalytics.setEnabled({ enabled: true })
        .then(() =>
          FirebaseAnalytics.setUserProperty({
            key: 'app_flavor',
            value: environment.flavor,
          })
        )
        .catch((error: unknown) => this.warn('Analytics initialization failed', error));

      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => {
          void this.trackScreen(event.urlAfterRedirects);
        });
    }

    if (this.telemetryConfig.crashlyticsEnabled) {
      void FirebaseCrashlytics.setEnabled({ enabled: true })
        .then(() => this.setCrashContext())
        .catch((error: unknown) => this.warn('Crashlytics initialization failed', error));

      window.addEventListener('unhandledrejection', (event) => {
        void this.recordException(event.reason, 'unhandled-promise-rejection');
      });
    }
  }

  async trackScreen(route: string): Promise<void> {
    if (!this.canUseAnalytics()) {
      return;
    }

    const screenName = this.normalizeRoute(route);
    try {
      await FirebaseAnalytics.setCurrentScreen({
        screenName,
        screenClassOverride: 'AngularRoute',
      });
    } catch (error) {
      this.warn('Screen tracking failed', error);
    }
  }

  async logEvent(
    name: string,
    parameters: TelemetryEventParameters = {}
  ): Promise<void> {
    if (!this.canUseAnalytics()) {
      return;
    }

    try {
      await FirebaseAnalytics.logEvent({
        name,
        params: {
          ...parameters,
          app_flavor: environment.flavor,
        },
      });
    } catch (error) {
      this.warn(`Analytics event failed: ${name}`, error);
    }
  }

  async recordException(error: unknown, source = 'unknown'): Promise<void> {
    if (!this.canUseCrashlytics()) {
      return;
    }

    const normalizedError = this.toError(error);
    const message = this.sanitizeText(normalizedError.message || normalizedError.name);
    const signature = `${source}|${message}|${this.normalizeRoute(this.router.url)}`;
    if (this.isDuplicateException(signature)) {
      return;
    }

    let stacktrace: CrashlyticsStackFrame[] | undefined;
    try {
      const frames = await StackTrace.fromError(normalizedError, { offline: true });
      stacktrace = frames.slice(0, 100).map((frame) => ({
        fileName: frame.fileName ? this.sanitizeText(frame.fileName) : undefined,
        functionName: frame.functionName
          ? this.sanitizeText(frame.functionName)
          : undefined,
        lineNumber: frame.lineNumber,
      }));
    } catch {
      stacktrace = undefined;
    }

    try {
      await FirebaseCrashlytics.recordException({
        message,
        domain: normalizedError.name || 'JavaScriptError',
        keysAndValues: [
          { key: 'source', value: source, type: 'string' },
          {
            key: 'route',
            value: this.normalizeRoute(this.router.url),
            type: 'string',
          },
          { key: 'app_flavor', value: environment.flavor, type: 'string' },
          { key: 'app_version', value: environment.appVersion, type: 'string' },
        ],
        stacktrace,
      });
    } catch (recordingError) {
      this.warn('Crashlytics exception recording failed', recordingError);
    }
  }

  async setUserIdentifier(userId: string | null): Promise<void> {
    if (!this.isAvailable()) {
      return;
    }

    const anonymousId = userId?.trim() || '';
    const operations: Promise<void>[] = [];

    if (this.telemetryConfig.analyticsEnabled) {
      operations.push(
        FirebaseAnalytics.setUserId({ userId: anonymousId || null })
      );
    }
    if (this.telemetryConfig.crashlyticsEnabled) {
      operations.push(FirebaseCrashlytics.setUserId({ userId: anonymousId }));
    }

    try {
      await Promise.all(operations);
    } catch (error) {
      this.warn('Telemetry user identifier update failed', error);
    }
  }

  private async setCrashContext(): Promise<void> {
    await Promise.all([
      FirebaseCrashlytics.setCustomKey({
        key: 'app_flavor',
        value: environment.flavor,
        type: 'string',
      }),
      FirebaseCrashlytics.setCustomKey({
        key: 'app_version',
        value: environment.appVersion,
        type: 'string',
      }),
      FirebaseCrashlytics.setCustomKey({
        key: 'platform',
        value: Capacitor.getPlatform(),
        type: 'string',
      }),
    ]);
  }

  private isAvailable(): boolean {
    return this.telemetryConfig.enabled && this.isNative && this.hasFirebaseConfig;
  }

  private canUseAnalytics(): boolean {
    return this.isAvailable() && this.telemetryConfig.analyticsEnabled;
  }

  private canUseCrashlytics(): boolean {
    return this.isAvailable() && this.telemetryConfig.crashlyticsEnabled;
  }

  private normalizeRoute(route: string): string {
    const withoutQuery = (route || '/unknown').split(/[?#]/, 1)[0] || '/unknown';
    const normalized = withoutQuery
      .replace(/^\/+/, '')
      .replace(/\/\d+(?=\/|$)/g, '/:id')
      .replace(/[^a-zA-Z0-9_/:.-]/g, '_');
    return (normalized || 'root').slice(0, 100);
  }

  private toError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }

    const nestedError = (error as { rejection?: unknown; ngOriginalError?: unknown } | null)
      ?.ngOriginalError ??
      (error as { rejection?: unknown } | null)?.rejection;
    if (nestedError instanceof Error) {
      return nestedError;
    }

    return new Error(this.sanitizeText(`${error ?? 'Unknown error'}`));
  }

  private sanitizeText(value: string): string {
    return value
      .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [redacted]')
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted-email]')
      .replace(/\b\d{7,19}\b/g, '[redacted-number]')
      .slice(0, 500);
  }

  private isDuplicateException(signature: string): boolean {
    const now = Date.now();
    const previous = this.recentExceptionSignatures.get(signature);
    this.recentExceptionSignatures.set(signature, now);

    if (this.recentExceptionSignatures.size > 100) {
      for (const [key, timestamp] of this.recentExceptionSignatures.entries()) {
        if (now - timestamp > 60_000) {
          this.recentExceptionSignatures.delete(key);
        }
      }
    }

    return previous !== undefined && now - previous < 60_000;
  }

  private warn(message: string, error: unknown): void {
    console.warn(`[Telemetry] ${message}`, error);
  }
}
